import { Stack } from "@fluentui/react";
import AnswerPreProcessForCodeTagHelper from "./AnswerPreProcessForCodeTagHelper";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { skipUnwantedTag, decodeHtmlEntities } from "../../api";

type AttachmentClickHandler = (filename: string, page?: number) => void;

export const AnswerWithPreCodeDisplay: React.FC<{
    sanitizedHTML: string;
    onAttachmentLinkClick?: AttachmentClickHandler;
}> = ({ sanitizedHTML, onAttachmentLinkClick }) => {
    const sanitizedHtmlContent = skipUnwantedTag(AnswerPreProcessForCodeTagHelper(sanitizedHTML));

    const escapeMarkdownLabel = (value: string): string => {
        return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\\/g, "\\\\").replace(/\[/g, "\\[").replace(/\]/g, "\\]");
    };

    const extractPageAndCleanFileName = (rawValue: string): { fileName: string; page?: number } => {
        let cleaned = decodeHtmlEntities(rawValue).trim();
        let page: number | undefined;

        const hashOrQueryMatch = cleaned.match(/[?#][^#?]*\b(?:page|pg|p)\s*=?\s*(\d+)\s*$/i);
        const parenMatch = cleaned.match(/\(\s*(?:page|pg|p)\s*[:=]?\s*(\d+)\s*\)\s*$/i);
        const bareMatch = cleaned.match(/\b(?:page|pg|p)\s*[:=]?\s*(\d+)\s*$/i);
        const match = hashOrQueryMatch || parenMatch || bareMatch;

        if (match?.[1]) {
            page = Number(match[1]);
            cleaned = cleaned.replace(match[0], "").trim();
        }

        cleaned = cleaned.replace(/[)\],.;:'"]+$/, "").trim();
        return { fileName: cleaned, page: Number.isFinite(page) ? page : undefined };
    };

    const parseAttachmentFromHref = (rawHref?: string): { fileName: string; page?: number } | null => {
        if (!rawHref) return null;
        const decodedHref = decodeHtmlEntities(rawHref).trim();
        if (!decodedHref) return null;

        if (decodedHref.startsWith("attachment://")) {
            const withoutScheme = decodedHref.replace("attachment://", "");
            const [encodedFile, query] = withoutScheme.split("?");
            let fileName = encodedFile;
            try {
                fileName = decodeURIComponent(encodedFile);
            } catch {
                fileName = encodedFile;
            }
            const parsed = extractPageAndCleanFileName(fileName);

            let page = parsed.page;
            if (query) {
                const queryPage = new URLSearchParams(query).get("page");
                if (queryPage && !Number.isNaN(Number(queryPage))) {
                    page = Number(queryPage);
                }
            }

            if (!parsed.fileName) return null;
            return { fileName: parsed.fileName, page: Number.isFinite(page) ? page : undefined };
        }

        if (/^(https?:|mailto:|tel:)/i.test(decodedHref)) return null;

        let candidate = decodedHref.replace(/^\.\//, "").replace(/^\//, "");
        if (candidate.toLowerCase().startsWith("attachments/")) {
            candidate = candidate.slice("attachments/".length);
        } else if (candidate.includes("/")) {
            const segments = candidate.split("/").filter(Boolean);
            candidate = segments[segments.length - 1] ?? "";
        }
        if (!candidate) return null;

        const parsed = extractPageAndCleanFileName(candidate);
        if (!parsed.fileName || !/\.[a-z0-9]{1,10}$/i.test(parsed.fileName)) return null;
        return parsed;
    };

    // Convert bare attachments/... tokens to markdown links.
    // Handles:
    // - attachments/file.ext#page=12
    // - attachments/file.ext?page=12
    // - attachments/file.ext (page 12) / p. 12 / pg 12
    //
    // Important: we avoid swallowing trailing punctuation like ")" "," "."
    const contentWithAttachmentLinks = sanitizedHtmlContent.replace(
        /(\[)?\s*(attachments\/[^<>\r\n\t]+(?:\.[a-z0-9]{1,10})+)(?:\s*\(?\s*(?:page|pg|p)\.?\s*[:=]?\s*(\d+)\s*\)?|[?#](?:[^\s<>()[\]]*?)\b(?:page|pg|p)\s*=?\s*(\d+))?\s*(\])?/gi,
        (_full: string, _openBracket: string, path: string, pageA?: string, pageB?: string) => {
            const filePath = path.replace(/^attachments\//i, "");
            const decodedPath = decodeHtmlEntities(filePath);
            const segments = decodedPath.split("/").filter(Boolean);
            const decodedFileName = segments[segments.length - 1] ?? decodedPath;
            const pageStr = pageA || pageB;
            const page = pageStr ? Number(pageStr) : undefined;

            const encodedFile = encodeURIComponent(decodedFileName);
            const href = Number.isFinite(page) ? `attachment://${encodedFile}?page=${page}` : `attachment://${encodedFile}`;
            const label = Number.isFinite(page) ? `${decodedFileName} (page=${page})` : decodedFileName;
            const safeLabel = escapeMarkdownLabel(label);

            return `[${safeLabel}](${href})`;
        }
    );

    const parseAttachmentLabel = (text: string): { fileName: string; page?: number } | null => {
        const trimmed = decodeHtmlEntities(text).trim();
        if (!trimmed) return null;

        let candidate = trimmed;
        if (candidate.toLowerCase().startsWith("attachments/")) {
            candidate = candidate.slice("attachments/".length);
        }

        const parsed = extractPageAndCleanFileName(candidate);
        if (parsed.fileName && /\.[a-z0-9]{1,10}$/i.test(parsed.fileName)) {
            const segments = parsed.fileName.split("/").filter(Boolean);
            const fileName = segments[segments.length - 1] ?? parsed.fileName;
            return { fileName, page: parsed.page };
        }
        return null;
    };

    // Parse <a class="supContainer" title="..."><sup>N</sup></a> patterns from a plain text string
    // and return a mixed array of strings and React citation elements.
    const parseCitationsFromString = (text: string): React.ReactNode => {
        const parts: React.ReactNode[] = [];
        // Support either attribute order (class first or title first)
        const citationPattern =
            /<a\s[^>]*\bclass="supContainer"[^>]*\btitle="([^"]*)"[^>]*><sup>(\d+)<\/sup><\/a>|<a\s[^>]*\btitle="([^"]*)"[^>]*\bclass="supContainer"[^>]*><sup>(\d+)<\/sup><\/a>/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = citationPattern.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }
            const title = match[1] ?? match[3];
            const num = match[2] ?? match[4];
            const extracted = title ? extractPageAndCleanFileName(title) : null;
            const fileName = extracted?.fileName;
            const page = extracted?.page;
            if (title && num && fileName) {
                parts.push(
                    <a
                        key={`citation-code-${match.index}`}
                        href="#"
                        className="supContainer"
                        title={title}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            onAttachmentLinkClick?.(fileName, page);
                        }}
                    >
                        <sup>{num}</sup>
                    </a>
                );
            } else {
                // title/num absent or fileName could not be resolved — emit the original HTML as text
                parts.push(match[0]);
            }
            lastIndex = citationPattern.lastIndex;
        }
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }
        return parts.length === 0 ? text : parts;
    };

    const allowUrl = (url: string) => {
        if (!url) return "";
        if (url.startsWith("attachment://")) return url;
        const isLikelyFile = /(?:^|\/)[^/]+(?:\.[a-z0-9]{1,10})+(?:[?#][^\s]*)?$/i.test(url) || url.toLowerCase().startsWith("attachments/");
        try {
            const parsed = new URL(url, window.location.origin);
            const isAbsoluteSafe = /^(https?:|mailto:|tel:)/i.test(url);
            if (isAbsoluteSafe && ["http:", "https:", "mailto:", "tel:"].includes(parsed.protocol)) return url;
            return isLikelyFile ? url : "";
        } catch {
            if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("mailto:") || url.startsWith("tel:")) {
                return url;
            }
            return isLikelyFile ? url : "";
        }
    };

    return (
        <Stack.Item
            grow
            style={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
                overflowX: "auto",
                maxWidth: "100%",
                fontFamily: `"BIZ UDPGothic", sans-serif, Arial`
            }}
        >
            <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
                urlTransform={allowUrl}
                components={{
                    a({ href, children, ...props }) {
                        // Handle citation links generated by AnswerParser:
                        // <a class="supContainer" title="path/to/file.pdf#page=N"><sup>N</sup></a>
                        const anchorClass = (props as { className?: string }).className;
                        const anchorTitle = (props as { title?: string }).title;
                        if (anchorClass === "supContainer" && anchorTitle) {
                            const { fileName, page } = extractPageAndCleanFileName(anchorTitle);
                            if (fileName) {
                                return (
                                    <a
                                        {...props}
                                        href="#"
                                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                            e.preventDefault();
                                            onAttachmentLinkClick?.(fileName, page);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    >
                                        {children}
                                    </a>
                                );
                            }
                        }

                        // helper to get text from children safely
                        const childText = Array.isArray(children)
                            ? children.map(c => (typeof c === "string" ? c : "")).join("")
                            : typeof children === "string"
                              ? children
                              : "";

                        // 1) Parse attachment from href first, then fallback to label parsing.
                        const attachmentFromHref = parseAttachmentFromHref(href);
                        const attachmentFromLabel = parseAttachmentLabel(childText);
                        let attachmentCandidate = attachmentFromHref ?? attachmentFromLabel ?? null;
                        let attachmentDisplayText: string | null = null;

                        // If href is file-like but label is just an ID (for example "[2602.11988v1]"),
                        // include the label prefix in filename matching as a fallback.
                        if (attachmentFromHref && !attachmentFromLabel && childText) {
                            const label = childText.trim();
                            if (label && !/\.[a-z0-9]{1,10}$/i.test(label)) {
                                const normalizedLabel = label.startsWith("[") && label.endsWith("]") ? label : `[${label}]`;
                                attachmentCandidate = {
                                    fileName: `${normalizedLabel} ${attachmentFromHref.fileName}`,
                                    page: attachmentFromHref.page
                                };
                                attachmentDisplayText = Number.isFinite(attachmentFromHref.page)
                                    ? `${normalizedLabel} ${attachmentFromHref.fileName} (page=${attachmentFromHref.page})`
                                    : `${normalizedLabel} ${attachmentFromHref.fileName}`;
                            }
                        }

                        if (attachmentCandidate) {
                            return (
                                <a
                                    {...props}
                                    href="#"
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                        e.preventDefault();
                                        onAttachmentLinkClick?.(attachmentCandidate.fileName, attachmentCandidate.page);
                                    }}
                                    style={{ color: "#0078d4", textDecoration: "underline", cursor: "pointer" }}
                                >
                                    {attachmentDisplayText ?? children}
                                </a>
                            );
                        }

                        // 2) Fallback: if some raw HTML anchor slips in and child text is "attachments/..."
                        if (childText.startsWith("attachments/")) {
                            const match = childText.match(/attachments\/[^<>\r\n\t]+(?:\.[a-z0-9]{1,10})+/i);
                            const rawText = match ? match[0] : childText;

                            let page: number | undefined;
                            const pageMatch = rawText.match(/\b(?:page|pg|p)\.?\s*[:=]?\s*(\d+)/i);
                            if (pageMatch?.[1]) page = Number(pageMatch[1]);

                            const fileName = rawText
                                .replace(/^attachments\//i, "")
                                .replace(/\s*\(?\b(?:page|pg|p)\.?\s*[:=]?\s*\d+\)?\s*$/i, "")
                                .replace(/[)\],.;:'"]+$/, "")
                                .trim();
                            const decodedFileName = decodeHtmlEntities(fileName);
                            const displayText = Number.isFinite(page) ? `${decodedFileName} (page=${page})` : decodedFileName;

                            return (
                                <a
                                    {...props}
                                    href="#"
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                        e.preventDefault();
                                        onAttachmentLinkClick?.(decodedFileName, page);
                                    }}
                                    style={{ color: "#0078d4", textDecoration: "underline", cursor: "pointer" }}
                                >
                                    {displayText}
                                </a>
                            );
                        }

                        // 3) Fallback: label looks like a file name (ReactMarkdown may strip custom hrefs)
                        const labelAttachment = parseAttachmentLabel(childText);
                        if (labelAttachment && (!href || href === "#" || !isSafeHref(href))) {
                            return (
                                <a
                                    {...props}
                                    href="#"
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                                        e.preventDefault();
                                        onAttachmentLinkClick?.(labelAttachment.fileName, labelAttachment.page);
                                    }}
                                    style={{ color: "#0078d4", textDecoration: "underline", cursor: "pointer" }}
                                >
                                    {children}
                                </a>
                            );
                        }

                        function isSafeHref(localHref?: string): boolean {
                            if (!localHref) return false;
                            try {
                                const url = new URL(localHref, window.location.origin);
                                return url.protocol === "http:" || url.protocol === "https:";
                            } catch {
                                return localHref.startsWith("http://") || localHref.startsWith("https://");
                            }
                        }

                        if (isSafeHref(href)) {
                            return (
                                <a {...props} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#0078d4", textDecoration: "underline" }}>
                                    {children}
                                </a>
                            );
                        }

                        return <span style={{ color: "#0078d4", textDecoration: "underline" }}>{children}</span>;
                    },
                    code({ className, children, ...props }) {
                        // Check if children contains React elements (e.g., citations from rehypeRaw
                        // parsing raw HTML <pre><code> blocks with embedded <a class="supContainer">).
                        const childArray = Array.isArray(children) ? children : [children];
                        const hasElements = childArray.some(c => c !== null && c !== undefined && typeof c === "object");

                        let codeContent: React.ReactNode;
                        if (hasElements) {
                            // Preserve React elements directly; decode any string segments
                            codeContent = childArray.map((child, i) => (typeof child === "string" ? decodeHtmlEntities(child) : child));
                        } else {
                            // Plain string children: decode entities then convert inline citation HTML
                            // (<a class="supContainer">) to real React elements so they render as links
                            const rawText =
                                typeof children === "string"
                                    ? decodeHtmlEntities(children)
                                    : Array.isArray(children)
                                      ? decodeHtmlEntities(children.join(""))
                                      : children;
                            codeContent = typeof rawText === "string" ? parseCitationsFromString(rawText) : rawText;
                        }

                        if (!className) {
                            return (
                                <code
                                    {...props}
                                    style={{
                                        backgroundColor: "#f4f4f4",
                                        padding: "2px 4px",
                                        borderRadius: "3px"
                                    }}
                                >
                                    {codeContent}
                                </code>
                            );
                        }

                        return (
                            <pre
                                style={{
                                    overflow: "auto",
                                    wordBreak: "break-word",
                                    padding: "10px",
                                    backgroundColor: "#f4f4f4",
                                    borderRadius: "5px"
                                }}
                            >
                                <code className={className} {...props}>
                                    {codeContent}
                                </code>
                            </pre>
                        );
                    },

                    table({ children, ...props }) {
                        return (
                            <table
                                {...props}
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    margin: "10px 0"
                                }}
                            >
                                {children}
                            </table>
                        );
                    },

                    tr({ children, ...props }) {
                        return (
                            <tr
                                {...props}
                                style={{
                                    borderBottom: "1px solid #ddd"
                                }}
                            >
                                {children}
                            </tr>
                        );
                    },

                    th({ children, ...props }) {
                        return (
                            <th
                                {...props}
                                style={{
                                    padding: "8px",
                                    backgroundColor: "#f2f2f2",
                                    border: "1px solid #ddd",
                                    textAlign: "left"
                                }}
                            >
                                {children}
                            </th>
                        );
                    },

                    td({ children, ...props }) {
                        return (
                            <td
                                {...props}
                                style={{
                                    padding: "8px",
                                    border: "1px solid #ddd",
                                    textAlign: "left"
                                }}
                            >
                                {children}
                            </td>
                        );
                    }
                }}
            >
                {contentWithAttachmentLinks}
            </ReactMarkdown>
        </Stack.Item>
    );
};
